import { useEffect, useMemo, useState } from 'react'
import {
  Link,
  Outlet,
  createFileRoute,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import {
  App as AntdApp,
  Avatar,
  Divider,
  Drawer,
  Layout,
  Menu,
  Popover,
  Segmented,
  Space,
  Tag,
  Typography,
  theme,
} from 'antd'
import type { MenuProps } from 'antd'
import {
  AndroidOutlined,
  ApiOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BulbOutlined,
  CloudSyncOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  DashboardOutlined,
  DownOutlined,
  FileDoneOutlined,
  FlagOutlined,
  FolderOpenOutlined,
  GiftOutlined,
  LinkOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  MessageOutlined,
  MobileOutlined,
  NotificationOutlined,
  SnippetsOutlined,
  ForkOutlined,
  PictureOutlined,
  ShoppingCartOutlined,
  ThunderboltOutlined,
  TranslationOutlined,
  TrophyOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { requireAdmin } from '@/auth/guards'
import { signOut, useAuthStore } from '@/auth/session'
import type { AdminClaims } from '@/auth/session'
import { useIsMobile } from '@/lib/hooks'
import { getNavigationDrawerWidth } from '@/lib/responsive'
import { useEffectiveTheme, useThemeStore } from '@/lib/theme'
import type { ThemeMode } from '@/lib/theme'
import type { FileRoutesByTo } from '../routeTree.gen'

/* ── Nav config (exported for future reuse) ───────────────────── */

export type AppPath = keyof FileRoutesByTo

export interface NavItem {
  path: AppPath
  label: string
  icon: React.ReactNode
}
export interface NavGroup {
  key: string
  label: string
  items: NavItem[]
}

export const TOP_NAV: NavItem[] = [
  { path: '/dashboard', label: '仪表盘', icon: <DashboardOutlined /> },
]

export const NAV_GROUPS: NavGroup[] = [
  {
    key: 'content',
    label: '内容',
    items: [
      { path: '/anime', label: '番剧', icon: <VideoCameraOutlined /> },
      { path: '/anime-types', label: '番剧类型', icon: <AppstoreOutlined /> },
      {
        path: '/anime-series',
        label: '番剧系列',
        icon: <FolderOpenOutlined />,
      },
      {
        path: '/stale-animes',
        label: '超期未更新',
        icon: <ClockCircleOutlined />,
      },
      {
        path: '/cross-media',
        label: '漫画关联',
        icon: <LinkOutlined />,
      },
      {
        path: '/tmdb',
        label: 'TMDB 元数据',
        icon: <ApiOutlined />,
      },
    ],
  },
  {
    key: 'moderation',
    label: '审核',
    items: [
      { path: '/comments', label: '评论审核', icon: <MessageOutlined /> },
      { path: '/danmaku', label: '弹幕审核', icon: <CommentOutlined /> },
      { path: '/reports', label: '举报', icon: <FlagOutlined /> },
      {
        path: '/exam-contributions',
        label: '试题投稿',
        icon: <FileDoneOutlined />,
      },
    ],
  },
  {
    key: 'feedback',
    label: '反馈',
    items: [
      {
        path: '/episode-feedback',
        label: '剧集反馈',
        icon: <NotificationOutlined />,
      },
      {
        path: '/product-feedback',
        label: '产品反馈',
        icon: <BulbOutlined />,
      },
      {
        path: '/reply-templates',
        label: '回复模板',
        icon: <SnippetsOutlined />,
      },
      {
        path: '/feedback-routes',
        label: '分流规则',
        icon: <ForkOutlined />,
      },
    ],
  },
  {
    key: 'ops',
    label: '运营',
    items: [
      { path: '/users', label: '用户', icon: <UserOutlined /> },
      { path: '/badges', label: '徽章', icon: <TrophyOutlined /> },
      { path: '/products', label: '商品', icon: <GiftOutlined /> },
      { path: '/orders', label: '订单', icon: <ShoppingCartOutlined /> },
      {
        path: '/payment-reconciliation',
        label: '补单查验',
        icon: <AuditOutlined />,
      },
      { path: '/ad-slots', label: '广告位', icon: <PictureOutlined /> },
      {
        path: '/android-ads',
        label: 'Android 广告',
        icon: <AndroidOutlined />,
      },
      {
        path: '/announcements',
        label: '公告',
        icon: <NotificationOutlined />,
      },
      {
        path: '/friend-links',
        label: '友情链接',
        icon: <LinkOutlined />,
      },
      {
        path: '/subtitle-translate',
        label: '字幕翻译',
        icon: <TranslationOutlined />,
      },
    ],
  },
  {
    key: 'system',
    label: '系统',
    items: [
      { path: '/app-versions', label: 'App 更新', icon: <MobileOutlined /> },
      {
        path: '/transcodes',
        label: '转码队列',
        icon: <ThunderboltOutlined />,
      },
      { path: '/cache', label: '缓存刷新', icon: <CloudSyncOutlined /> },
      {
        path: '/rss-notifications',
        label: 'RSS 通知',
        icon: <NotificationOutlined />,
      },
      { path: '/audit', label: '审计', icon: <AuditOutlined /> },
    ],
  },
]

const ALL_PATHS: AppPath[] = [
  ...TOP_NAV.map((n) => n.path),
  ...NAV_GROUPS.flatMap((g) => g.items.map((n) => n.path)),
]

/* ── Shell state helpers ──────────────────────────────────────── */

const COLLAPSE_KEY = 'xifan.admin.sidebar.collapsed'
const SIDEBAR_ID = 'admin-sidebar'

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1'
  } catch {
    return false
  }
}

function useViewportWidth(): number {
  const [width, setWidth] = useState(() =>
    typeof window === 'undefined' ? 1024 : window.innerWidth,
  )
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return width
}

/* ── Route ────────────────────────────────────────────────────── */

export const Route = createFileRoute('/_authed')({
  beforeLoad: requireAdmin,
  component: AdminShell,
})

function AdminShell() {
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const claims = useAuthStore((s) => s.claims)
  const { message } = AntdApp.useApp()
  const { token } = theme.useToken()

  // < DESKTOP_SIDER_BREAKPOINT(992)：侧栏换成抽屉，与表格→卡片断点一致
  const isMobile = useIsMobile()
  const viewportWidth = useViewportWidth()
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [userPopoverOpen, setUserPopoverOpen] = useState(false)
  const themeMode = useThemeStore((s) => s.mode)
  const setThemeMode = useThemeStore((s) => s.setMode)
  const effective = useEffectiveTheme()

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0')
    } catch {
      /* noop */
    }
  }, [collapsed])

  // Auto-close drawer on route change (Drawer doesn't auto-dismiss)
  useEffect(() => {
    setDrawerOpen(false)
  }, [pathname])

  const selectedKeys = useMemo(() => {
    let match: AppPath | undefined
    for (const path of ALL_PATHS) {
      if (pathname === path || pathname.startsWith(path + '/')) {
        if (!match || path.length > match.length) match = path
      }
    }
    return match ? [match] : []
  }, [pathname])

  const menuItems = useMemo<MenuProps['items']>(() => {
    const toItem = (n: NavItem) => ({
      key: n.path,
      icon: n.icon,
      label: (
        <Link to={n.path} style={{ color: 'inherit' }}>
          {n.label}
        </Link>
      ),
    })
    return [
      ...TOP_NAV.map(toItem),
      { type: 'divider' as const },
      ...NAV_GROUPS.map((g) => ({
        type: 'group' as const,
        key: g.key,
        label: g.label,
        children: g.items.map(toItem),
      })),
    ]
  }, [])

  async function handleLogout() {
    setUserPopoverOpen(false)
    await signOut()
    message.success('已退出登录')
    navigate({ to: '/login', replace: true })
  }

  const menu = (
    <Menu
      mode="inline"
      theme={effective}
      items={menuItems}
      selectedKeys={selectedKeys}
      style={{ borderRight: 0, paddingTop: 8 }}
      className="admin-shell__menu"
    />
  )

  const username = claims?.username ?? ''
  const initial = (username.charAt(0) || '?').toUpperCase()
  const year = new Date().getFullYear()

  return (
    <Layout className="admin-shell" style={{ minHeight: '100vh' }}>
      <a href="#admin-main" className="admin-shell__skip-link">
        跳转到主内容
      </a>

      {!isMobile && (
        <Layout.Sider
          id={SIDEBAR_ID}
          width={220}
          collapsedWidth={64}
          collapsed={collapsed}
          collapsible
          trigger={null}
          theme={effective}
          style={{
            background: token.colorBgContainer,
            borderRight: `1px solid ${token.colorBorderSecondary}`,
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflow: 'auto',
          }}
        >
          <Link
            to="/dashboard"
            className="admin-shell__sider-brand"
            aria-label="返回仪表盘"
          >
            <span className="admin-shell__sider-brand-mark">稀</span>
            {!collapsed ? (
              <span className="admin-shell__sider-brand-copy">
                <Typography.Text
                  strong
                  ellipsis
                  style={{ color: token.colorText, lineHeight: '20px' }}
                >
                  稀饭动漫
                </Typography.Text>
                <Typography.Text
                  type="secondary"
                  ellipsis
                  style={{ fontSize: 12, lineHeight: '18px' }}
                >
                  管理中心
                </Typography.Text>
              </span>
            ) : null}
          </Link>
          {menu}
        </Layout.Sider>
      )}

      {isMobile && (
        <Drawer
          id={SIDEBAR_ID}
          className="admin-shell__nav-drawer"
          // AntD 6 的 className 落在内层 .ant-drawer-section，宽度覆盖需挂在 root 上
          rootClassName="admin-shell__nav-drawer-root"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          placement="left"
          width={getNavigationDrawerWidth(viewportWidth)}
          title="导航"
          styles={{ body: { padding: 0 } }}
        >
          {menu}
        </Drawer>
      )}

      <Layout>
        <Layout.Header
          className="admin-shell__header"
          style={{
            background: token.colorBgContainer,
            height: 56,
            paddingLeft: 8,
            paddingRight: 16,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 10,
          }}
        >
          <Space size={4} align="center">
            <button
              type="button"
              className="admin-shell__icon-btn"
              onClick={() =>
                isMobile ? setDrawerOpen((v) => !v) : setCollapsed((v) => !v)
              }
              aria-label={
                isMobile ? '打开导航' : collapsed ? '展开侧边栏' : '收起侧边栏'
              }
              aria-expanded={isMobile ? drawerOpen : !collapsed}
              aria-controls={SIDEBAR_ID}
            >
              {isMobile ? (
                <MenuOutlined />
              ) : collapsed ? (
                <MenuUnfoldOutlined />
              ) : (
                <MenuFoldOutlined />
              )}
            </button>
            <Link
              to="/dashboard"
              className="admin-shell__brand-link"
              aria-label="返回仪表盘"
            >
              {isMobile ? (
                /* 手机顶栏宽度有限：品牌标 + 短名，不再出现「稀饭动漫·管…」截断 */
                <span className="admin-shell__brand-compact">
                  <span
                    className="admin-shell__sider-brand-mark"
                    aria-hidden="true"
                  >
                    稀
                  </span>
                  <Typography.Text
                    strong
                    style={{ fontSize: 15, color: token.colorText }}
                  >
                    管理中心
                  </Typography.Text>
                </span>
              ) : (
                <Typography.Text
                  strong
                  style={{ fontSize: 14, color: token.colorText }}
                >
                  稀饭动漫·管理中心
                </Typography.Text>
              )}
            </Link>
          </Space>

          <Popover
            trigger="click"
            placement="bottomRight"
            arrow={false}
            open={userPopoverOpen}
            onOpenChange={setUserPopoverOpen}
            overlayClassName="admin-shell__user-popover"
            content={
              <UserPanel
                claims={claims}
                themeMode={themeMode}
                onSetTheme={setThemeMode}
                onLogout={handleLogout}
                initial={initial}
              />
            }
          >
            <button
              type="button"
              className="admin-shell__user-btn"
              aria-label="账户"
              aria-haspopup="dialog"
              aria-expanded={userPopoverOpen}
            >
              <Avatar
                size={28}
                src={claims?.picture ?? undefined}
                alt=""
                style={{ background: token.colorPrimary, fontSize: 13 }}
              >
                {initial}
              </Avatar>
              <span className="admin-shell__user-email" title={claims?.email}>
                {username || claims?.email}
              </span>
              <DownOutlined
                style={{ fontSize: 10, color: token.colorTextTertiary }}
              />
            </button>
          </Popover>
        </Layout.Header>

        <Layout.Content
          id="admin-main"
          className="admin-shell__content"
          tabIndex={-1}
          style={{
            padding: 24,
            background: token.colorBgLayout,
            flex: 1,
            outline: 'none',
          }}
        >
          <Outlet />
        </Layout.Content>

        <Layout.Footer
          className="admin-shell__footer"
          style={{
            background: token.colorBgContainer,
            borderTop: `1px solid ${token.colorBorderSecondary}`,
            padding: '12px 24px',
            textAlign: 'center',
            color: token.colorTextTertiary,
            fontSize: 12,
            lineHeight: '20px',
          }}
        >
          <Space
            size="middle"
            split={
              <span style={{ opacity: 0.4 }} aria-hidden="true">
                ·
              </span>
            }
          >
            <span>v0.2</span>
            {import.meta.env.DEV ? (
              <Tag color="orange" style={{ margin: 0 }}>
                DEV
              </Tag>
            ) : null}
            <span>© {year} 稀饭动漫</span>
          </Space>
        </Layout.Footer>
      </Layout>
    </Layout>
  )
}

/* ── User panel (popover content) ─────────────────────────────── */

function UserPanel({
  claims,
  themeMode,
  onSetTheme,
  onLogout,
  initial,
}: {
  claims: AdminClaims | null
  themeMode: ThemeMode
  onSetTheme: (m: ThemeMode) => void
  onLogout: () => void
  initial: string
}) {
  return (
    <div className="admin-shell__user-panel" role="dialog" aria-label="账户">
      <div className="admin-shell__user-panel__identity">
        <Avatar
          size={40}
          src={claims?.picture ?? undefined}
          alt=""
          style={{ background: 'var(--ant-color-primary)', fontSize: 16 }}
        >
          {initial}
        </Avatar>
        <div className="admin-shell__user-panel__name">
          <div
            className="admin-shell__user-panel__display-name"
            title={claims?.username}
          >
            {claims?.username || '未命名用户'}
          </div>
          <div className="admin-shell__user-panel__email" title={claims?.email}>
            {claims?.email}
          </div>
        </div>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      <div className="admin-shell__user-panel__appearance">
        <div className="admin-shell__user-panel__label">外观</div>
        <Segmented
          size="small"
          block
          value={themeMode}
          onChange={(v) => onSetTheme(v as ThemeMode)}
          options={[
            { label: '跟随系统', value: 'system' },
            { label: '浅色', value: 'light' },
            { label: '深色', value: 'dark' },
          ]}
        />
      </div>

      <Divider style={{ margin: '8px 0' }} />

      <button
        type="button"
        className="admin-shell__user-panel__logout"
        onClick={onLogout}
      >
        <LogoutOutlined />
        <span>退出登录</span>
      </button>
    </div>
  )
}
